import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { Button, Checkbox, Col, Divider, Drawer, Icon, List, Modal, Row } from "ant-design-vue";
import { MediaSlot, MediaType } from "@/util/enum";
import { fmtMsg, MessageHelper, NotificationType, PrimaryLanguageLocale } from "commonui";
import { MediaUploaderLocale } from "@/locales/localeid";
import { getModule } from "vuex-module-decorators";
import { MediaUploadState } from "@/modules/media-upload";
import { MediaSlotBinding, MediaTypeBinding } from "../utils/const";
import { MediaSlotTypeMap, MediaStatusTypeMap } from "@/util/constants";
import Spinning from "@/components/spinning/spinning.vue";
import {
	ContentMediaSlotUploadInfoModel,
	ContentMediaSlotStatusInfoModel,
	ExistingMediaInfo,
	HasPdfContentResponseModel,
	MediaFile,
	MediaUploadService,
	SaveMediaUploadRequestModel,
	UploadMediaFileRequestModel
} from "@/services/media-uploader";
import { v4 } from "uuid";
import FileUploader from "./component/file-uploader/file-uploader.vue";

import TitleListState from "@/modules/title-list";
import { ContentService } from "@/services/content";
import { DateHelper } from "@/util/helper";
import { minBy } from "lodash";
@Component({
	components: { Button, Drawer, Icon, FileUploader, Checkbox, Spinning, Divider, Row, Col, List, Item: List.Item }
})
export default class MediaUploader extends Vue {
	@Prop() uploadDrawerVisible?: boolean;
	@Prop()
	mediaSlotId!: MediaSlot;
	@Prop({ default: () => {} }) onDrawerClose!: (isSave: boolean) => void;
	@Prop() currentComponentId!: string;
	@Prop() pdfSlots!: HasPdfContentResponseModel[];

	private mediaUploadStore = getModule(MediaUploadState);
	private titleListStore = getModule(TitleListState);

	public loading: boolean = true;
	public disableSaveButton: boolean = true;
	public drawerTitle: string = "Edit Media";
	public breakIntoImages: boolean = false;
	public disableBreakImages: boolean = false;
	public keepExistingTiming: boolean = false;
	public disableExistingTiming: boolean = true;
	public selectedMediaSlot: MediaSlot = MediaSlot.ViewingContent;
	public isUploadedFileExists: boolean = false;

	public file!: MediaFile;
	public allFiles: MediaFile[] = [];
	public multilingualFiles: MediaFile[] = [];
	public isUploadStarted = false;
	public mediaToRemove: string[] = [];
	public mediaSlotStatus: string = "";
	public showWarning: boolean = false;
	public isOkClicked: boolean = true;

	created() {
		this.mediaUploadStore.getDownloadSasURL();
	}

	async setExistingMediaInfo() {
		if (this.mediaSlotId != MediaSlot.IndividualAudio && this.currentComponentId) {
			ContentService.GetMediaSlots(this.currentComponentId, this.mediaSlotId)
				.then((data: ContentMediaSlotUploadInfoModel) => {
					if (data) {
						this.disableExistingTiming = !data.isPageTimingExist;

						if (data.mediaSlots && data.mediaSlots.length) {
							this.allFiles.map((slotFile) => {
								const matched = data.mediaSlots.find(
									(a) =>
										((!a.languageCode && slotFile.language == PrimaryLanguageLocale.English) ||
											a.languageCode == slotFile.language) &&
										!a.isPublished
								);

								if (matched) {
									slotFile.id = matched.mediaId;
									slotFile.isExisting = true;
									slotFile.uploadedAt = matched.uploadedAt;
									slotFile.fileName = matched.fileName;
									slotFile.size = matched.size;
									slotFile.downloadUrl = matched.url;
									slotFile.mediaStatusId = matched.mediaStatusId;

									if (slotFile.id == this.file.id) {
										this.file = slotFile;
									}
								} else {
									const publishedRevision = data.mediaSlots.find(
										(a) =>
											((!a.languageCode && slotFile.language == PrimaryLanguageLocale.English) ||
												a.languageCode == slotFile.language) &&
											a.isPublished
									);

									if (publishedRevision) {
										slotFile.fileName = publishedRevision.fileName;
										slotFile.size = publishedRevision.size;
										slotFile.mediaStatusId = publishedRevision.mediaStatusId;
										slotFile.publishedMediaRevision = {
											mediaId: publishedRevision.mediaId,
											uploadedAt: publishedRevision.uploadedAt,
											downloadUrl: publishedRevision.url
										} as ExistingMediaInfo;
									}
								}
							});

							this.mediaSlotStatus = this.getMediaSlotStatus(this.allFiles);
						}
					}

					this.loading = false;
				})
				.catch(() => {
					//this.loading = false;
				});
		}
		else if(this.mediaSlotId === MediaSlot.IndividualAudio && this.currentComponentId) {
			await ContentService.GetMediaSlotStatus(this.currentComponentId, [MediaSlot.IndividualAudio])
				.then((data: ContentMediaSlotStatusInfoModel) => {
					if(data.mediaSlots.length) {
						this.allFiles = [];
						this.allFiles = data.mediaSlots.map(
							(a) => {
								var tempFile: MediaFile = {
									id: a.isPublished ? v4() : a.mediaId,
									isExisting: true,
									uploadedAt: a.uploadedAt,
									fileName: a.fileName,
									size: a.size,
									downloadUrl: a.url,
									language: a.languageCode,
									mediaStatusId: a.mediaStatusId,
									isMultilingual: false,
									publishedMediaRevision: a.isPublished ? {mediaId: a.mediaId, uploadedAt: a.uploadedAt, downloadUrl: a.url} as ExistingMediaInfo : undefined,
									pageId: a.pageId
								}
							return tempFile
						})
						this.mediaSlotStatus = this.getMediaSlotStatus(this.allFiles);
					}
					this.loading = false;
				})
		}
	}

	unmounted() {
		this.resetUpload();
	}

	get slotId() {
		return this.mediaSlotId;
	}

	getInitialFileObj(languageCode: PrimaryLanguageLocale, isMultilingual = false) {
		return {
			id: v4(),
			language: languageCode,
			isMultilingual: isMultilingual,
			isExisting: false,
			publishedMediaRevision: undefined,
			progress: undefined,
			uploadedAt: undefined,
			length: undefined
		} as MediaFile;
	}
	@Watch("mediaSlotId")
	initializeFile() {
		if (!this.mediaSlotId || !this.currentComponentId) return;

		this.resetUpload();

		if (this.mediaSlotId == MediaSlot.ViewingContent || this.mediaSlotId === MediaSlot.TeacherManual) {
			this.breakIntoImages = true;
			this.disableBreakImages = false;
		}

		this.file = this.getInitialFileObj(PrimaryLanguageLocale.English);

		this.allFiles = [this.file];
		this.multilingualFiles = [];

		if (this.showMultilingual()) {
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Japanese, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Chinese, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Korean, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Malaysia, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Mongolian, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Russian, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Arabic, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Spanish, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Thai, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Vietnamese, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Myanmar, true));
			this.multilingualFiles.push(this.getInitialFileObj(PrimaryLanguageLocale.Khmer, true));

			this.allFiles = this.allFiles.concat(this.multilingualFiles);
		}

		this.setExistingMediaInfo();
	}

	get getSlotName() {
		return this.$format(MediaSlotBinding[this.mediaSlotId]);
	}
	get getMediaTypeName() {
		return this.$format(MediaTypeBinding[MediaSlotTypeMap[this.mediaSlotId]]);
	}

	get mediaUploaderLocale() {
		return MediaUploaderLocale;
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	async saveMediaUploadHandler() {
		this.loading = true;

		await ContentService.ContentScheduled(this.currentComponentId).then((scheduledAt: string) => {
			if (scheduledAt) {
				this.loading = false;
				const scheduleDateText = DateHelper.formatDate2Local(scheduledAt);
				const confirmMessage = fmtMsg(MediaUploaderLocale.MediaUploadScheduledWarning, {
					scheduled: scheduleDateText
				});

				Modal.confirm({
					okText: "Ok",
					title: confirmMessage,
					onOk: this.uploadHandler,
					onCancel: this.onCancel
				});
			} else {
				this.uploadHandler();
			}
		});
	}

	async uploadHandler() {
		const slotId = this.mediaSlotId;
		this.mediaUploadStore.resetState();
		this.disableBreakImages = true;
		this.isUploadStarted = true;
		this.loading = true;
		const allPromisses: Promise<void>[] = [];

		if(this.mediaToRemove.length && (this.mediaSlotId === MediaSlot.ViewingContent || this.mediaSlotId === MediaSlot.AudioBackground || this.mediaSlotId === MediaSlot.TeacherManual)) {
			this.showWarning = await MediaUploadService.HasPageMedia({ mediaId: this.mediaToRemove[0] })
			if(this.showWarning) {
				this.isOkClicked = false;
				this.showWarningModal();
			}
		}

		if (this.allFiles && this.allFiles.length) {
			const fileToUpload = this.allFiles.filter((a) => a.file);
			fileToUpload.map((fileItem) => {
				if (fileItem.file) {
					allPromisses.push(
						this.mediaUploadStore.uploadFile({
							id: fileItem.id,
							file: fileItem.file,
							languageCode: fileItem.language,
							length: fileItem.length,
							slotId: slotId,
							pageId: fileItem.pageId
						})
					);
				}
			});

			Promise.all(allPromisses).then(this.saveMediaUploadDetail);
		} else {
			this.saveMediaUploadDetail();
		}
	}

	async saveMediaUploadDetail() {
		const data = {
			contentId: this.currentComponentId,
			breakPages: this.breakIntoImages,
			keepPageTimings: this.keepExistingTiming,
			mediaSlotId: this.mediaSlotId
		} as SaveMediaUploadRequestModel;

		const fileData: UploadMediaFileRequestModel[] = [];

		this.mediaUploadStore.fileUploads.map((file) => {
			fileData.push({
				id: file.id,
				fileName: file.fileName,
				languageCode: file.languageCode,
				uploadedFilePath: file.filePath,
				size: file.size,
				length: file.length,
				pageId: file.pageId
			} as UploadMediaFileRequestModel);
		});

		data.uploadedFiles = fileData;
		data.removeMediaIds = this.mediaToRemove;

		if(this.isOkClicked)
		{
			MediaUploadService.SaveMediaUpload(data)
				.then((res) => {
					this.loading = false;
					const successMsg = fmtMsg(MediaUploaderLocale.MediaUploadSuccessMsg);
					MessageHelper.Message(NotificationType.Success, successMsg);
					this.onDrawerClose(true);
					this.resetUpload();
				})
				.catch((er) => {
					this.loading = false;
					this.mediaUploadStore.resetState();
				});
		}
	}

	showWarningModal() {
		Modal.confirm({
			title: fmtMsg(MediaUploaderLocale.MediaUploaderWarningModalTitleText),
			content: fmtMsg(MediaUploaderLocale.MediaUploaderWarningModalBodyText),
			maskClosable: true,
			onOk: () => {
				this.onModalOk();
			},
			onCancel:() => {
				this.onModalCancel();
			}
		});
	}

	onModalOk() {
		this.isOkClicked = true;
		this.saveMediaUploadDetail()
	}

	onModalCancel() {
		this.isOkClicked = false;
		this.loading = false;
		this.showWarning = false;
		this.onDrawerClose(true);
		this.resetUpload();
	}

	showMultilingual() {
		return (
			this.titleListStore.editedContentDetails.isMultilingual &&
			(this.mediaSlotId == MediaSlot.ViewingContent || this.mediaSlotId == MediaSlot.AlternateContent)
		);
	}

	resetUpload() {
		this.mediaUploadStore.resetState();
		this.loading = true;
		this.allFiles = [];
		this.multilingualFiles = [];
		this.disableSaveButton = true;
		this.mediaToRemove = [];
		this.isUploadStarted = false;
		this.breakIntoImages = false;
		this.disableBreakImages = false;
		this.keepExistingTiming = false;
		this.disableExistingTiming = true;
	}

	onCancel() {
		// add cancel here
		this.loading = false;
	}

	onFileUpload(uploadedFile: MediaFile) {
		this.isUploadedFileExists = true;
		this.disableSaveButton = false;
		const fileItem = this.allFiles?.find((a) => a.id == uploadedFile.id);
		if (fileItem) {
			fileItem.file = uploadedFile.file;
		}

		// remove item if it already exists in removed list
		if (this.mediaToRemove.find((a) => a == uploadedFile.id)) {
			this.mediaToRemove = this.mediaToRemove.filter((a) => a != uploadedFile.id);
		}
	}

	setBreakIntoImages(value) {
		this.breakIntoImages = value.target.checked;
	}

	setKeepExistingTiming(value) {
		this.keepExistingTiming = value.target.checked;
	}

	removeMedia(id: string) {
		this.disableSaveButton = false;
		// if not alreay exists
		if (this.mediaToRemove.findIndex((a) => a == id) < 0) {
			const matched = this.allFiles.find((a) => a.id == id);

			if (matched && matched.isExisting) {
				this.mediaToRemove.push(id);
			} else if (matched) {
				matched.file = undefined;
			} else if (!matched) {
				const publishedMatch = this.allFiles.find(
					(a) => a.publishedMediaRevision && a.publishedMediaRevision?.mediaId == id
				);

				if (publishedMatch) {
					this.mediaToRemove.push(id);

					// publishedMatch.publishedMediaRevision = undefined;
				}
			}
		}

		this.isUploadedFileExists = this.allFiles.filter((a) => a.file).length > 0;
	}

	get showPageBreakSection() {
		if (
			this.mediaSlotId &&
			(this.mediaSlotId === MediaSlot.ViewingContent || this.mediaSlotId === MediaSlot.TeacherManual)
		) {
			return MediaSlotTypeMap[this.mediaSlotId] == MediaType.PDF;
		}
		return false;
	}

	getMediaSlotStatus(allFiles: MediaFile[]) {
		let mediaStatus: string = "";

		if (allFiles && allFiles.length) {
			const mediaSlotStatus = minBy(allFiles, "mediaStatusId")?.mediaStatusId;
			mediaStatus = mediaSlotStatus ? MediaStatusTypeMap[mediaSlotStatus] : "";
		}

		return mediaStatus;
	}

	get mediaStatusId() {
		const mediaStatusIds = this.allFiles
			.filter((f) => Object.keys(f).some((x) => x === "mediaStatusId"))
			.map((f) => f.mediaStatusId) as number[];
		return mediaStatusIds.length ? Math.min(...mediaStatusIds) : -1;
	}
}
